import { Check, GraduationCap, Building2, Users, Calendar, BadgeCheck } from 'lucide-react';

interface InviteSummaryProps {
  kind: 'role' | 'license' | null;
  role: string | null;
  licenseName: string | null;
  schoolName: string | null;
  licenseType: string | null;
  seatsAvailable: number | null;
  maxSeats: number | null;
  expiresAt: string | null;
}

const humanizeLicenseType = (t: string | null): string => {
  if (!t) return 'Organization license';
  const map: Record<string, string> = {
    university: 'University license',
    team: 'Team license',
    enterprise: 'Enterprise license',
    organization: 'Organization license',
  };
  return map[t.toLowerCase()] ?? `${t.charAt(0).toUpperCase()}${t.slice(1)} license`;
};

const formatDate = (iso: string | null): string | null => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return null;
  }
};

export const InviteSummary = ({
  kind,
  role,
  licenseName,
  schoolName,
  licenseType,
  seatsAvailable,
  maxSeats,
  expiresAt,
}: InviteSummaryProps) => {
  // Legacy role-only codes get the simple confirmation
  if (kind !== 'license') {
    return (
      <div className="flex items-center gap-2 p-2 bg-emerald-500/10 border border-emerald-500/30 rounded-md">
        <Check className="w-4 h-4 text-emerald-500" />
        <span className="text-sm text-emerald-500 capitalize">
          Valid {role ?? ''} invite code
        </span>
      </div>
    );
  }

  const expiresDisplay = formatDate(expiresAt);
  const showSchool = !!schoolName && schoolName !== licenseName;

  return (
    <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3 space-y-2">
      <div className="flex items-center gap-2">
        <Check className="w-4 h-4 text-emerald-500" />
        <span className="text-sm font-medium text-emerald-500">Valid invite code</span>
      </div>

      <div className="space-y-1.5 text-sm pl-6">
        {licenseName && (
          <div className="flex items-start gap-2">
            <Building2 className="w-3.5 h-3.5 mt-0.5 text-muted-foreground shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="text-muted-foreground">Joining: </span>
              <span className="text-foreground font-medium break-words">{licenseName}</span>
            </div>
          </div>
        )}

        {showSchool && (
          <div className="flex items-start gap-2">
            <GraduationCap className="w-3.5 h-3.5 mt-0.5 text-muted-foreground shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="text-muted-foreground">School: </span>
              <span className="text-foreground break-words">{schoolName}</span>
            </div>
          </div>
        )}

        <div className="flex items-start gap-2">
          <BadgeCheck className="w-3.5 h-3.5 mt-0.5 text-muted-foreground shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="text-muted-foreground">Type: </span>
            <span className="text-foreground">{humanizeLicenseType(licenseType)}</span>
            {role && (
              <>
                <span className="text-muted-foreground"> • Role: </span>
                <span className="text-foreground capitalize">{role}</span>
              </>
            )}
          </div>
        </div>

        {seatsAvailable != null && maxSeats != null && (
          <div className="flex items-start gap-2">
            <Users className="w-3.5 h-3.5 mt-0.5 text-muted-foreground shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="text-muted-foreground">Seats: </span>
              <span className="text-foreground">
                {seatsAvailable} of {maxSeats} available
              </span>
            </div>
          </div>
        )}

        {expiresDisplay && (
          <div className="flex items-start gap-2">
            <Calendar className="w-3.5 h-3.5 mt-0.5 text-muted-foreground shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="text-muted-foreground">Expires: </span>
              <span className="text-foreground">{expiresDisplay}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InviteSummary;
